import { hrtime } from "@use/universal-hrtime";

import { urls } from "@courses-pm2/icons/src/ts/config";

/**
 * Hardcoded font family name which we're going to use as a font-family for
 * rendering custom icons.
 */
const FONT_FAMILY = "gyldendal-pm2-icon-font";

/**
 * Specifies the timeout after which we're going to kill the attempt to load the
 * custom font family.
 */
const TIMEOUT = 10000;

/**
 * We're going to cache the request to fetch the custom icon font, so we avoid
 * having it fetched multiple times.
 */
let cache: Promise<string> | undefined;

/**
 * Helper that'll fetch the custom icon font package, and returns a promise
 * that'll be resolved with the applied font-family name once the icons are
 * available for use.
 */
export async function getIconFont(): Promise<string> {
    if (!cache) {
        // Register when we started fetching the custom font family, so we can
        // reject the promise in case it times out!
        const startedAt = hrtime();

        // Cache the promise to load the font family, so we can re-use it later
        // on
        cache = new Promise<string>((resolve, reject) => {
            const css = `
                @font-face {
                    font-family: "${ FONT_FAMILY}";
                    src:
                        url('${ urls.icons}.eot'),
                        url('${ urls.icons}.eot?#iefix') format('embedded-opentype'),
                        url('${ urls.icons}.woff2') format('woff2'),
                        url('${ urls.icons}.woff') format('woff'),
                        url('${ urls.icons}.ttf') format('truetype'),
                        url('${ urls.icons}.svg#${FONT_FAMILY}') format('svg');
                }
            `;

            // Inject the CSS into the browser as a custom generated stylesheet
            const style = document.createElement("style");

            style.type = "text/css";
            style.setAttribute("data-from", "pm2-icons");
            style.appendChild(document.createTextNode(css));

            document.head.appendChild(style);

            // Create an element, which we're going to use in order to actually
            // load the font family
            const fontFamilyPreloader = document.createElement("div");

            fontFamilyPreloader.style.cssText = `
                position: absolute;
                top: -100px;
                left: -100px;
                font-family: ${ FONT_FAMILY}, verdana;
                font-size: 50px;
                line-height: 1em;
                opacity: 0;
                pointer-events: none;
                user-select: none;
            `;

            fontFamilyPreloader.innerText = "l";

            // Mount the preload-element into the DOM, so that the browser will
            // begin fetching the custom font family file
            document.body.appendChild(fontFamilyPreloader);

            // Will contain a reference to the interval that's used to
            // constantly check if the icon font has been fully loaded
            let fontStatusInterval: number | undefined;

            // Helper that'll stop the interval that checks if the font has
            // been loaded so we don't keep on pushing the CPU
            const stopFontStatusInterval = () => {
                // Unmount preloader from the DOM, so we avoid any unwanted
                // bloating...
                document.body.removeChild(fontFamilyPreloader);

                // ... And actually stop the interval from running
                window.clearInterval(fontStatusInterval);
            };

            // Helper that'll be executed once every 50ms to review if the font
            // has been loaded
            const checkFontStatus = () => {
                // Return the generated name of the font-family once the
                // custom font is loaded and available for rendering
                if (fontFamilyPreloader.offsetWidth > 20) {
                    resolve(FONT_FAMILY);

                    // Stop the interval that checks if font is available
                    stopFontStatusInterval();
                }

                // If more than the maximum allowed timeout has passed by
                // now, then bail out!
                if (hrtime() > startedAt + TIMEOUT) {
                    reject(new Error("getIconFont(): Request timed out."));

                    // Stop the interval that checks if font is available
                    stopFontStatusInterval();
                }
            };

            // Dispatch the interval
            fontStatusInterval = window.setInterval(checkFontStatus, 50);
        });
    }

    return cache;
}
